import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UserGroup } from 'diwimamodelweb/lib/network/user-group.data';
import { isArray, deepCopy } from 'diwimamodelweb/lib';
import { PersonService } from '../person.service';
import { SettingsService } from '../settings.service';
import { UserFeedbackService } from '../user-feedback.service';
import { anyToUser } from '../utils/utils';
import { UserGroupsService } from '../../cockpit/us-managment/user-groups.service';

export interface UserMultiEditDialogData {
  users: any[];
}

/** Bulk-assign selected users to a user group (add-only, existing memberships stay). */
@Component({
  selector: 'app-user-multi-edit-dialog',
  templateUrl: './user-multi-edit-dialog.component.html',
  styleUrls: ['./user-multi-edit-dialog.component.css'],
})
export class UserMultiEditDialogComponent implements OnInit, OnDestroy {

  groupId = '';
  groups: UserGroup[] = [];
  saving = false;

  private sub?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<UserMultiEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UserMultiEditDialogData,
    private settingsService: SettingsService,
    private personService: PersonService,
    private translateService: TranslateService,
    private userFeedbackService: UserFeedbackService,
    private usGroupService: UserGroupsService,
  ) {}

  private get isSuperContext(): boolean {
    return !!this.settingsService.superContext?.value;
  }

  ngOnInit(): void {
    const ctx = this.settingsService.selectedUserContext$?.value;
    if (!this.usGroupService.allAssignableUserGroupsSubject$.value) {
      this.usGroupService.observeDataGroups(ctx?.identifier);
    }
    this.sub = this.usGroupService.allAssignableUserGroupsSubject$.subscribe(list => {
      this.groups = list ?? [];
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get userCount(): number {
    return this.data?.users?.length ?? 0;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  async confirm(): Promise<void> {
    if (!this.groupId || this.saving) return;
    this.saving = true;
    try {
      this.userFeedbackService.loading.emit(true);
      for (const user of this.data.users) {
        const newUser = anyToUser(deepCopy(user));
        newUser.userGroupIds = isArray(user.userGroupIds)
          ? (user.userGroupIds.includes(this.groupId) ? user.userGroupIds : [...user.userGroupIds, this.groupId])
          : [this.groupId];
        await this.personService.updateExistingUser(user, newUser);
      }
      this.userFeedbackService.loading.emit(false);
      const sbSuccessSave = this.translateService.instant('changes_saved');
      this.userFeedbackService.showSnackBar.emit(sbSuccessSave);
      this.dialogRef.close(true);
    } catch (error) {
      console.error(error);
      this.userFeedbackService.loading.emit(false);
      const m = this.translateService.instant('error_saving');
      this.userFeedbackService.showSnackBar.emit(m);
    } finally {
      this.saving = false;
    }
  }
}
